/**
 * 클래스 관리(/admin|/master …/class-management) 목록 필터를 URL·session·상세 복귀에 공유
 * 쿼리: q(검색), ct(open|org), st(active|inactive), p(페이지)
 */
const storageKey = (basePath: string) => `classListReturn:${basePath}`;

export const CLASS_LIST_RETURN_STATE_KEY = 'classListReturnQuery' as const;

export type ClassManagementListState = {
	q: string;
	classType: 'all' | 'open' | 'organization';
	active: 'all' | 'active' | 'inactive';
	page: number;
};

export function parseListQueryString(search: string | URLSearchParams): ClassManagementListState {
	const sp = typeof search === 'string' ? new URLSearchParams(search.replace(/^\?/, '')) : search;
	const q = sp.get('q') ?? '';
	const ct = sp.get('ct');
	const classType: ClassManagementListState['classType'] =
		ct === 'open' ? 'open' : ct === 'org' ? 'organization' : 'all';
	const st = sp.get('st');
	const active: ClassManagementListState['active'] =
		st === 'active' || st === 'inactive' ? st : 'all';
	let page = Math.floor(Number(sp.get('p')));
	if (!Number.isFinite(page) || page < 1) page = 1;
	if (page > 1_000_000) page = 1_000_000;
	return { q, classType, active, page };
}

/** 목록 쿼리(맨 앞 `?` 포함) — 기본이면 `''` */
export function buildListQueryString(s: ClassManagementListState): string {
	const p = new URLSearchParams();
	const t = s.q.trim();
	if (t) p.set('q', t);
	if (s.classType === 'open') p.set('ct', 'open');
	if (s.classType === 'organization') p.set('ct', 'org');
	if (s.active === 'active' || s.active === 'inactive') p.set('st', s.active);
	if (s.page > 1) p.set('p', String(s.page));
	const out = p.toString();
	return out ? `?${out}` : '';
}

export function saveClassManagementListQueryToStorage(basePath: string, query: string): void {
	if (typeof sessionStorage === 'undefined') {
		return;
	}
	const key = storageKey(basePath);
	if (!query || query === '?' || query === '') {
		try {
			sessionStorage.removeItem(key);
		} catch {
			// no-op
		}
		return;
	}
	const normalized = query.startsWith('?') ? query : `?${query}`;
	try {
		sessionStorage.setItem(key, normalized);
	} catch {
		// no-op
	}
}

/**
 * 상세(목록으로) — navigate state 우선, 없으면 sessionStorage(목록이 마지막으로 쓴 값)
 */
export function getClassManagementListReturnQuery(
	basePath: string,
	locationState: unknown
): string {
	const s = (locationState as { [CLASS_LIST_RETURN_STATE_KEY]?: string } | null | undefined)?.[CLASS_LIST_RETURN_STATE_KEY];
	if (typeof s === 'string' && s.length) {
		return s.startsWith('?') ? s : `?${s}`;
	}
	if (typeof sessionStorage === 'undefined') {
		return '';
	}
	try {
		const raw = sessionStorage.getItem(storageKey(basePath));
		if (raw) {
			return raw.startsWith('?') ? raw : `?${raw}`;
		}
	} catch {
		// no-op
	}
	return '';
}
